import { getAuth } from 'firebase/auth';
import {
  collection,
  doc,
  Firestore,
  getDocs,
  query,
  Timestamp,
  updateDoc,
  where,
} from 'firebase/firestore';

import { FormSchema, normalizeFormSchema } from '../types/formSchema';
import type { Tryout, TryoutWithId } from '../types/tryout';

const TRYOUTS_COLLECTION = 'tryouts';

const CREATE_TRYOUT_URL =
  'https://us-central1-cheereval.cloudfunctions.net/createTryout';

/** Initial options for athlete position pills in the New Tryout dialog (persisted on create). */
export const DEFAULT_ATHLETE_POSITIONS: readonly string[] = [
  'Unknown',
  'Top',
  'Base',
  'Third',
  'Multi',
];

/** Initial options for athlete status pills in the New Tryout dialog (persisted on create). */
export const DEFAULT_ATHLETE_STATUSES: readonly string[] = ['New', 'Returning'];

function readTeams(data: Record<string, unknown>): string[] {
  if (!Array.isArray(data.teams)) {
    return [];
  }
  return data.teams.filter((t): t is string => typeof t === 'string');
}

function readAthletePositions(data: Record<string, unknown>): string[] {
  if (!Array.isArray(data.athletePositions)) {
    return [];
  }
  return data.athletePositions.filter((t): t is string => typeof t === 'string');
}

function readAthleteStatuses(data: Record<string, unknown>): string[] {
  if (!Array.isArray(data.athleteStatuses)) {
    return [];
  }
  return data.athleteStatuses.filter((t): t is string => typeof t === 'string');
}

function readFormSchema(value: unknown): FormSchema | undefined {
  if (typeof value !== 'object' || value === null) {
    return undefined;
  }
  const candidate = value as Record<string, unknown>;
  if (!Array.isArray(candidate.groups)) {
    return undefined;
  }
  return normalizeFormSchema({ groups: candidate.groups as FormSchema['groups'] });
}

function readTryoutDoc(data: Record<string, unknown>): Omit<Tryout, 'createdBy'> & { createdBy: string | undefined } {
  return {
    active: typeof data.active === 'boolean' ? data.active : false,
    code: typeof data.code === 'string' ? data.code : '',
    createdAt: data.createdAt instanceof Timestamp ? data.createdAt : null,
    createdBy: typeof data.createdBy === 'string' ? data.createdBy : undefined,
    name: typeof data.name === 'string' ? data.name : '',
    teams: readTeams(data),
    athletePositions: readAthletePositions(data),
    athleteStatuses: readAthleteStatuses(data),
    formSchema: readFormSchema(data.formSchema),
    formName: typeof data.formName === 'string' ? data.formName : undefined,
    gymLogoId:
      typeof data.gymLogoId === 'string'
        ? data.gymLogoId
        : data.gymLogoId === null
          ? null
          : undefined,
    gymLogoUrl:
      typeof data.gymLogoUrl === 'string'
        ? data.gymLogoUrl
        : data.gymLogoUrl === null
          ? null
          : undefined,
    password: typeof data.password === 'string' ? data.password : undefined,
  };
}

export type TryoutListItem = TryoutWithId;

export interface CreateTryoutOptions {
  teams?: string[];
  athletePositions?: string[];
  athleteStatuses?: string[];
  /** Set only when the owner picks a logo (`string`) or explicitly “No logo” (`null`). */
  gymLogoId?: string | null;
  /** Same as on tryout doc — set with `gymLogoId` for scoring-coach UIs. */
  gymLogoUrl?: string | null;
}

export class CreateTryoutError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'CreateTryoutError';
  }
}

export async function createTryout(
  name: string,
  password: string,
  formSchema: FormSchema = { groups: [] },
  formName = '',
  options?: CreateTryoutOptions,
): Promise<{ code: string; tryoutId: string; password: string }> {
  const user = getAuth().currentUser;
  if (!user) {
    throw new CreateTryoutError('You must be signed in to create a tryout.');
  }
  const idToken = await user.getIdToken();

  const requestBody = {
    name,
    password,
    formSchema,
    formName,
    teams: options?.teams,
    athletePositions: options?.athletePositions,
    athleteStatuses: options?.athleteStatuses,
    ...(options?.gymLogoId !== undefined ? { gymLogoId: options.gymLogoId } : {}),
    ...(options?.gymLogoUrl !== undefined ? { gymLogoUrl: options.gymLogoUrl } : {}),
  };

  let response: globalThis.Response;
  try {
    response = await fetch(CREATE_TRYOUT_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${idToken}`,
      },
      body: JSON.stringify(requestBody),
    });
  } catch (err) {
    console.error('[createTryout] fetch failed:', err);
    throw new CreateTryoutError(
      'Could not reach the server. Please check your connection and try again.',
    );
  }

  if (!response.ok) {
    let message = 'Failed to create tryout. Please try again.';
    try {
      const body = (await response.json()) as { error?: string };
      if (typeof body.error === 'string' && body.error.length > 0) {
        message = body.error;
      }
    } catch {
      console.error('[createTryout] server returned HTTP', response.status, '(no JSON body)');
    }
    throw new CreateTryoutError(message);
  }

  const data = (await response.json()) as { code: string; password: string; tryoutId: string };
  return { code: data.code, tryoutId: data.tryoutId, password: data.password };
}

/** Replace the embedded evaluation form name and schema on an existing tryout. */
export async function updateTryoutForm(
  db: Firestore,
  tryoutId: string,
  formName: string,
  schema: FormSchema,
): Promise<void> {
  const tryoutRef = doc(db, TRYOUTS_COLLECTION, tryoutId);
  await updateDoc(tryoutRef, { formName, formSchema: schema });
}

export async function fetchTryoutsByUser(
  db: Firestore,
  userId: string,
): Promise<TryoutListItem[]> {
  const tryoutsRef = collection(db, TRYOUTS_COLLECTION);
  const q = query(tryoutsRef, where('createdBy', '==', userId));
  const snapshot = await getDocs(q);

  const items: TryoutListItem[] = [];

  for (const docSnap of snapshot.docs) {
    const raw = docSnap.data() as Record<string, unknown>;
    const parsed = readTryoutDoc(raw);

    items.push({
      id: docSnap.id,
      active: parsed.active,
      code: parsed.code,
      createdAt: parsed.createdAt,
      createdBy: parsed.createdBy ?? userId,
      name: parsed.name,
      teams: parsed.teams,
      athletePositions: parsed.athletePositions,
      athleteStatuses: parsed.athleteStatuses,
      formSchema: parsed.formSchema,
      formName: parsed.formName,
      gymLogoId: parsed.gymLogoId,
      gymLogoUrl: parsed.gymLogoUrl,
      password: parsed.password,
    });
  }

  items.sort((a, b) => {
    const aTime = a.createdAt?.toMillis() ?? 0;
    const bTime = b.createdAt?.toMillis() ?? 0;
    return bTime - aTime;
  });

  return items;
}

export async function updateTryoutName(
  db: Firestore,
  tryoutId: string,
  name: string,
): Promise<void> {
  const tryoutRef = doc(db, TRYOUTS_COLLECTION, tryoutId);
  await updateDoc(tryoutRef, { name });
}

export async function updateTryoutTeams(
  db: Firestore,
  tryoutId: string,
  teams: string[],
): Promise<void> {
  const tryoutRef = doc(db, TRYOUTS_COLLECTION, tryoutId);
  await updateDoc(tryoutRef, { teams });
}

export async function updateTryoutAthletePositions(
  db: Firestore,
  tryoutId: string,
  athletePositions: string[],
): Promise<void> {
  const tryoutRef = doc(db, TRYOUTS_COLLECTION, tryoutId);
  await updateDoc(tryoutRef, { athletePositions });
}

export async function updateTryoutAthleteStatuses(
  db: Firestore,
  tryoutId: string,
  athleteStatuses: string[],
): Promise<void> {
  const tryoutRef = doc(db, TRYOUTS_COLLECTION, tryoutId);
  await updateDoc(tryoutRef, { athleteStatuses });
}

export async function updateTryoutGymLogo(
  db: Firestore,
  tryoutId: string,
  gymLogoId: string | null,
  gymLogoUrl: string | null,
): Promise<void> {
  const tryoutRef = doc(db, TRYOUTS_COLLECTION, tryoutId);
  if (gymLogoId === null) {
    await updateDoc(tryoutRef, { gymLogoId: null, gymLogoUrl: null });
  } else {
    await updateDoc(tryoutRef, { gymLogoId, gymLogoUrl });
  }
}
